import { getIndexerGrpcArchiverApi } from '@/app/Services'
import { LeaderboardDuration, HistoricalPortfolioDuration } from '@/types'
import type { PnlLeaderboard, VolLeaderboard } from '@injectivelabs/sdk-ts'

type LeaderboardStoreState = {
  pnlLeaderboard?: PnlLeaderboard
  competitionLeaderboard?: PnlLeaderboard | VolLeaderboard
  historicalPnl: {
    time: number
    value: number
  }[]

  historicalVolume: {
    time: number
    value: number
  }[]

  historicalBalance: {
    time: number
    value: number
  }[]
}

const initialStateFactory = (): LeaderboardStoreState => ({
  pnlLeaderboard: undefined,
  competitionLeaderboard: undefined,
  historicalBalance: [],
  historicalPnl: [],
  historicalVolume: []
})

export const useLeaderboardStore = defineStore('leaderboard', {
  state: (): LeaderboardStoreState => initialStateFactory(),
  actions: {
    fetchPnlLeaderboard: lazyPiniaAction(
      () => import('./pnlLeaderboard'),
      'fetchPnlLeaderboard'
    ),
    fetchCompetitionLeaderboard: lazyPiniaAction(
      () => import('./pnlLeaderboard'),
      'fetchCompetitionLeaderboard'
    ),

    async fetchHistoricalBalance(
      resolution: HistoricalPortfolioDuration = HistoricalPortfolioDuration.OneMonth
    ) {
      const indexerGrpcArchiverApi = await getIndexerGrpcArchiverApi()

      const leaderboardStore = useLeaderboardStore()
      const sharedWalletStore = useSharedWalletStore()

      const { t, v } = await indexerGrpcArchiverApi.fetchHistoricalBalance({
        account: sharedWalletStore.injectiveAddress,
        resolution
      })

      const historicalBalance = t
        .map((time, index) => {
          return {
            time: time * 1000,
            value: v[index]
          }
        })
        .filter(
          (item): item is { time: number; value: number } =>
            item.value !== undefined
        )

      leaderboardStore.historicalBalance = historicalBalance.reverse()
    },

    async fetchHistoricalPnl(
      resolution: HistoricalPortfolioDuration = HistoricalPortfolioDuration.OneMonth
    ) {
      const indexerGrpcArchiverApi = await getIndexerGrpcArchiverApi()

      const leaderboardStore = useLeaderboardStore()
      const sharedWalletStore = useSharedWalletStore()

      const { t, v } = await indexerGrpcArchiverApi.fetchHistoricalRpnl({
        account: sharedWalletStore.injectiveAddress,
        resolution
      })

      const historicalPnl = t
        .map((time, index) => {
          return {
            time: time * 1000,
            value: v[index]
          }
        })
        .filter(
          (item): item is { time: number; value: number } =>
            item.value !== undefined
        )

      leaderboardStore.historicalPnl = historicalPnl.reverse()
    },

    async fetchHistoricalVolume(
      resolution: LeaderboardDuration = LeaderboardDuration.OneWeek
    ) {
      const indexerGrpcArchiverApi = await getIndexerGrpcArchiverApi()

      const leaderboardStore = useLeaderboardStore()
      const sharedWalletStore = useSharedWalletStore()

      const { t, v } = await indexerGrpcArchiverApi.fetchHistoricalVolumes({
        account: sharedWalletStore.injectiveAddress,
        resolution
      })

      const historicalVolume = t
        .map((time, index) => {
          return {
            time: time * 1000,
            value: v[index]
          }
        })
        .filter(
          (item): item is { time: number; value: number } =>
            item.value !== undefined
        )

      leaderboardStore.historicalVolume = historicalVolume.reverse()
    }
  }
})
